import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer, useGuideRequest } from '@/components/public';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sentinel URL used in the injected inline CTA. A body-level click delegate
// intercepts clicks on this and opens the GuideRequest modal instead of navigating.
const RISK_CHECK_SENTINEL = '#open-guide-request';

const DISCLAIMER =
  'This is a founder-facing note, not a legal opinion or legal advice. StartEase Agent is not a law firm. Verify statute text before any client-specific decision. Companies named as category examples illustrate the product space, not any violation.';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

// Injects a Risk Check CTA block into the sanitized body HTML, after the 2nd </h2>
const injectRiskCTA = (html) => {
  if (!html) return '';
  let count = 0;
  let injectionIndex = -1;
  const re = /<\/h2>/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    count += 1;
    if (count === 2) {
      injectionIndex = m.index + m[0].length;
      break;
    }
  }
  if (injectionIndex === -1) return html;
  const cta = `<div class="lw-inline-cta" data-testid="lw-inline-cta"><p><strong>Building in one of these categories?</strong> Send me one message about what you're building and I'll name the laws you're inside — which documents they demand, and what covers you. Free, usually within a day or two. <a href="${RISK_CHECK_SENTINEL}" class="lw-cta-link" data-guide-trigger="1">Start here.</a></p><p class="lw-inline-cta-from">— Santosh</p></div>`;
  return html.slice(0, injectionIndex) + cta + html.slice(injectionIndex);
};

const LawWatchArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { open: openGuide } = useGuideRequest();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toc, setToc] = useState([]);
  const articleRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API}/law-watch/${slug}`);
        if (!cancelled) setArticle(res.data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Delegate clicks on the injected inline CTA link (sentinel href) to open the modal.
  useEffect(() => {
    if (!article || !articleRef.current) return;
    const el = articleRef.current;
    const handler = (e) => {
      const a = e.target.closest('a[data-guide-trigger="1"]');
      if (!a) return;
      e.preventDefault();
      openGuide(`law-watch:${article.slug}`);
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [article, openGuide]);

  // Build TOC from H2 elements in rendered body
  useEffect(() => {
    if (!article || !articleRef.current) return;
    const h2s = articleRef.current.querySelectorAll('h2');
    const list = [];
    h2s.forEach((h, i) => {
      const id = `sec-${i}-${(h.textContent || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`;
      h.id = id;
      list.push({ id, text: h.textContent });
    });
    setToc(list);
  }, [article]);

  const bodyHtml = useMemo(() => (article ? injectRiskCTA(article.body_html) : ''), [article]);

  const articleJsonLd = useMemo(() => {
    if (!article) return null;
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': article.title,
      'description': article.hook,
      'author': { '@type': 'Person', 'name': 'Santosh Singh' },
      'publisher': {
        '@type': 'Organization',
        'name': 'StartEase Agent',
        'url': 'https://starteaseai.com',
      },
      'datePublished': article.published_at,
      'dateModified': article.verified_at || article.published_at,
      'mainEntityOfPage': `https://starteaseai.com/ai-law-watch/${article.slug}`,
    });
  }, [article]);

  const faqJsonLd = useMemo(() => {
    if (!article || !article.faq || article.faq.length === 0) return null;
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': article.faq.map((qa) => ({
        '@type': 'Question',
        'name': qa.question,
        'acceptedAnswer': { '@type': 'Answer', 'text': qa.answer },
      })),
    });
  }, [article]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-white flex flex-col" data-testid="law-watch-404">
        <Header />
        <div className="flex-1 max-w-[680px] mx-auto px-7 py-24 text-center">
          <h1 className="font-serif text-4xl font-semibold mb-4">Article not found</h1>
          <p className="text-slate-400 mb-8">This article either does not exist or has been unpublished.</p>
          <button
            onClick={() => navigate('/ai-law-watch')}
            className="text-[#059669] underline underline-offset-4"
            data-testid="law-watch-back-to-index"
          >
            Back to AI Law Watch
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading || !article) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-white" data-testid="law-watch-article-loading">
        <Header />
        <div className="max-w-[680px] mx-auto px-7 py-20">
          <p className="text-slate-500">Loading article…</p>
        </div>
      </div>
    );
  }

  const canonical = `https://starteaseai.com/ai-law-watch/${article.slug}`;

  return (
    <div className="min-h-screen bg-[#0b1310] text-white" data-testid="law-watch-article">
      <SEOHead
        title={`${article.title} | AI Law Watch — StartEase Agent`}
        description={article.hook}
        canonical={canonical}
      />
      {/* eslint-disable-next-line react/no-danger */}
      {articleJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleJsonLd }} />}
      {/* eslint-disable-next-line react/no-danger */}
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />}

      <Header />

      {/* Article styles — scoped by .lw-body */}
      <style>{`
        .lw-body h2{font-family:Georgia,serif;font-weight:600;font-size:25px;margin:42px 0 16px;line-height:1.25;color:#ffffff;scroll-margin-top:96px}
        .lw-body h3{font-family:Georgia,serif;font-weight:600;font-size:19px;margin:32px 0 10px;color:#ffffff}
        .lw-body p{font-size:16.5px;line-height:1.75;margin-bottom:22px;color:#a8ada4}
        .lw-body p strong,.lw-body strong{color:#ffffff;font-weight:600}
        .lw-body a{color:#059669;text-decoration:underline;text-underline-offset:3px}
        .lw-body ul,.lw-body ol{margin:0 0 22px 20px;color:#a8ada4;font-size:16.5px;line-height:1.75}
        .lw-body li{margin-bottom:8px}
        .lw-body blockquote{margin:26px 0;padding:16px 22px;border-left:3px solid rgba(255,255,255,0.15);background:#101b14;border-radius:0 10px 10px 0;color:#a8ada4;font-size:15px;line-height:1.7}
        .lw-body blockquote.case-callout{border-left-color:#e8a13c}
        .lw-body blockquote.case-callout p:first-child strong:first-child{color:#e8a13c}
        .lw-body table{width:100%;border-collapse:collapse;margin:22px 0;font-size:14.5px}
        .lw-body table th,.lw-body table td{padding:10px 14px;border-bottom:1px solid rgba(255,255,255,0.08);text-align:left;color:#a8ada4}
        .lw-body table th{color:#ffffff;font-weight:600}
        .lw-body code{background:#101b14;padding:2px 6px;border-radius:4px;font-size:14px}
        .lw-inline-cta{margin:38px 0;border:1px solid rgba(255,255,255,0.10);border-radius:10px;padding:22px 26px;background:#101b14}
        .lw-inline-cta p{font-size:15px;line-height:1.7;margin-bottom:0;color:#a8ada4}
        .lw-inline-cta p strong{color:#ffffff}
        .lw-inline-cta .lw-cta-link,.lw-inline-cta a{color:#059669;text-decoration:underline;text-underline-offset:3px}
        .lw-inline-cta-from{font-style:italic;margin-top:8px !important;font-size:13.5px !important;color:#767b73 !important}
      `}</style>

      <div className="max-w-[680px] mx-auto px-7 relative">
        {/* Article header */}
        <header className="pt-14 pb-0">
          <div className="text-[13px] text-slate-500 mb-4" data-testid="law-watch-breadcrumb">
            <Link to="/ai-law-watch" className="text-[#059669]">AI Law Watch</Link>
            <span> / </span>
            <span className="text-[#e8a13c] font-medium">{article.jurisdiction}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold leading-[1.16] mb-3.5" data-testid="law-watch-h1">
            {article.title}
          </h1>
          <p className="text-lg text-slate-400 leading-[1.55] mb-6">{article.hook}</p>
          <div className="flex flex-wrap justify-between gap-1.5 text-[13px] text-slate-500 border-t border-white/10 border-b border-white/5 py-3.5">
            <span>By <span className="text-white font-medium">Santosh Singh</span> · {formatDate(article.published_at)}</span>
            {article.verified_at && <span>Checked against sources {formatDate(article.verified_at)}</span>}
          </div>
        </header>

        {/* Fact-box */}
        {article.factbox && article.factbox.length > 0 && (
          <div className="my-8 border border-white/10 rounded-[10px] bg-[#101b14] overflow-hidden" data-testid="law-watch-factbox">
            <div className="text-[12px] font-semibold tracking-wide px-5 py-3 border-b border-white/5 text-slate-400 uppercase">
              The law at a glance
            </div>
            <table className="w-full text-sm">
              <tbody>
                {article.factbox.map((row, i) => (
                  <tr key={i} className={i < article.factbox.length - 1 ? 'border-b border-white/5' : ''}>
                    <td className="w-[140px] text-slate-500 text-[13px] px-5 py-2.5 align-top">{row.label}</td>
                    <td className="px-5 py-2.5 text-white leading-[1.55]">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Body */}
        <article
          ref={articleRef}
          className="lw-body"
          data-testid="law-watch-body"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />

        {/* FAQ — plain Q&A, no accordion (SEO-friendly, per design note) */}
        {article.faq && article.faq.length > 0 && (
          <section className="mt-10" data-testid="law-watch-faq">
            <h2 className="font-serif text-2xl font-semibold mt-10 mb-4">Questions founders ask</h2>
            {article.faq.map((qa, i) => (
              <div key={i} className="mb-5">
                <h3 className="text-[16.5px] font-semibold text-white mb-2">{qa.question}</h3>
                <p className="text-[15.5px] leading-[1.7] text-slate-400">{qa.answer}</p>
              </div>
            ))}
          </section>
        )}

        {/* Sources */}
        {article.sources && article.sources.length > 0 && (
          <section className="my-10 pt-4 border-t border-white/5" data-testid="law-watch-sources">
            <div className="text-[12px] font-semibold tracking-wide text-slate-400 uppercase mb-2.5">Source trail</div>
            <p className="text-[13.5px] leading-[2] text-slate-500">
              {article.sources.map((s, i) => (
                <React.Fragment key={i}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-[#059669] underline underline-offset-[3px]"
                  >
                    {s.title}
                  </a>
                  {i < article.sources.length - 1 && ' · '}
                </React.Fragment>
              ))}
            </p>
          </section>
        )}

        {/* Disclaimer — every article, from spec */}
        <p className="text-[12.5px] italic text-slate-500 leading-[1.7] my-8" data-testid="law-watch-disclaimer">
          {DISCLAIMER}
        </p>

        {/* Author box */}
        <div className="border-t border-white/10 py-5 pb-16 text-[14.5px] leading-[1.7] text-slate-400" data-testid="law-watch-author">
          <span className="text-white font-medium">Santosh Singh</span> helps founders form US companies
          and keep their AI products legal. He tracks 74 US AI laws across 84 startup domains — new ones
          land here first.
        </div>
      </div>

      {/* Sticky TOC (desktop only) — built from H2s after render */}
      {toc.length > 1 && (
        <aside className="hidden xl:block fixed top-32 right-8 w-56 text-[12.5px] text-slate-500" data-testid="law-watch-toc">
          <div className="uppercase tracking-wide text-slate-400 mb-3 text-[11px] font-semibold">On this page</div>
          <ul className="space-y-2">
            {toc.map((t) => (
              <li key={t.id}>
                <a href={`#${t.id}`} className="hover:text-white leading-[1.4] block">{t.text}</a>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <Footer />
    </div>
  );
};

export default LawWatchArticlePage;
